import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authClient } from '../lib/authClient';

// Import Generic Palettes
import { PREMIUM_PALETTES } from '../components/layouts/palettes';
import { normalizeLandingContent } from '../landing-templates/landingContent';
import { getTemplate } from '../landing-templates';
import { PreviewLanguageProvider } from '../landing-templates/preview/previewLanguageShim';
import LandingContentEditor from '../components/LandingContentEditor';
import AssetImageField from '../components/AssetImageField';
import API_BASE_URL from '../config/api';

const PREVIEW_ARTBOARD_WIDTH = 1280;

function buildSavePayload(cfg) {
  const { content, ...rest } = cfg;
  return {
    ...rest,
    content: {
      ...content,
      heroTitle: content?.hero?.title,
      heroSubtitle: content?.hero?.subtitle,
      heroParagraph: content?.hero?.paragraph,
      ctaText: content?.hero?.ctaText,
      features: content?.benefits?.items,
    },
  };
}

function BuilderSection({ title, open, onToggle, children, isDarkMode }) {
  return (
    <div className={`rounded-lg border ${isDarkMode ? 'border-line bg-canvas/30' : 'border-line bg-surface '}`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors rounded-lg ${
          isDarkMode ? 'text-ink hover:bg-surface/30' : 'text-ink hover:bg-canvas'
        }`}
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''} ${isDarkMode ? 'text-ink-muted' : 'text-ink-muted'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className={`px-4 pb-4 pt-1 flex flex-col gap-3 border-t ${isDarkMode ? 'border-line' : 'border-line'}`}>
          {children}
        </div>
      )}
    </div>
  );
}

function LayoutThumbnail({ type }) {
  if (type === 'split') {
    return (
      <svg viewBox="0 0 48 32" className="w-full h-8" aria-hidden="true">
        <rect x="2" y="4" width="44" height="24" rx="3" fill="currentColor" opacity="0.15" />
        <rect x="4" y="7" width="24" height="18" rx="2" fill="currentColor" opacity="0.35" />
        <rect x="30" y="7" width="14" height="18" rx="2" fill="currentColor" opacity="0.55" />
      </svg>
    );
  }
  if (type === 'tactical') {
    return (
      <svg viewBox="0 0 48 32" className="w-full h-8" aria-hidden="true">
        <rect x="6" y="4" width="36" height="24" rx="1" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6" />
        <rect x="10" y="10" width="20" height="3" fill="currentColor" opacity="0.5" />
        <rect x="10" y="16" width="28" height="3" fill="currentColor" opacity="0.35" />
        <rect x="10" y="22" width="28" height="4" fill="currentColor" opacity="0.7" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 48 32" className="w-full h-8" aria-hidden="true">
      <rect x="10" y="4" width="28" height="24" rx="4" fill="currentColor" opacity="0.2" />
      <rect x="16" y="10" width="16" height="3" rx="1" fill="currentColor" opacity="0.5" />
      <rect x="14" y="15" width="20" height="2" rx="1" fill="currentColor" opacity="0.35" />
      <rect x="14" y="20" width="20" height="4" rx="2" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

export default function LandingPageConfigBuilder() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [isNewCompany, setIsNewCompany] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLangTab, setActiveLangTab] = useState('hebrew');
  
  // UI States
  const [showAllPalettes, setShowAllPalettes] = useState(false);
  const [previewZoom, setPreviewZoom] = useState('fit');
  const [fitScale, setFitScale] = useState(0.6);
  const [contentHeight, setContentHeight] = useState(720);
  const [showScarcityPreview, setShowScarcityPreview] = useState(true);
  const [openSections, setOpenSections] = useState({
    layout: true,
    branding: false,
    colors: false,
    content: true,
    publish: true,
  });
  const previewContainerRef = useRef(null);
  const previewArtboardRef = useRef(null);

  const BUILDER_API_URL = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

  const normalizeLandingConfig = (saved = {}) => {
    if (!saved || typeof saved !== 'object') return {};

    if (saved.ui || saved.content) {
      return {
        ui: saved.ui || {},
        content: saved.content || {},
      };
    }

    if (saved.landing && typeof saved.landing === 'object') {
      return normalizeLandingConfig(saved.landing);
    }

    if (saved.layout || saved.themeSettings || saved.content) {
      return {
        ui: {
          ...(saved.layout ? { layout: saved.layout } : {}),
          ...(saved.themeSettings ? { themeSettings: saved.themeSettings } : {}),
        },
        content: saved.content || {},
      };
    }

    return {};
  };

  const hasSavedLandingConfig = (saved = {}) => {
    const normalized = normalizeLandingConfig(saved);
    const hasUi = normalized.ui && typeof normalized.ui === 'object' && Object.keys(normalized.ui).length > 0;
    const hasContent = normalized.content && typeof normalized.content === 'object' && Object.keys(normalized.content).length > 0;
    return hasUi || hasContent;
  };

  const mergeBuilderConfig = (saved = {}) => {
    const normalized = normalizeLandingConfig(saved);
    return {
      ui: {
        layout: normalized.ui?.layout || 'centered',
        branding: {
          logoUrl: normalized.ui?.branding?.logoUrl || '',
        },
        themeSettings: {
          colors: {
            ...PREMIUM_PALETTES[0].colors,
            ...(normalized.ui?.themeSettings?.colors || {}),
          },
        },
      },
      content: normalizeLandingContent(normalized.content || {}),
    };
  };

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';
  const previewContentDir = activeLangTab === 'hebrew' ? 'rtl' : 'ltr';

  const shell = {
    page: isDarkMode ? 'bg-canvas text-ink' : 'bg-moss text-ink',
    sidebar: isDarkMode ? 'bg-canvas border-line' : 'bg-surface border-line',
    previewPanel: isDarkMode ? 'bg-canvas' : 'bg-moss',
    previewToolbar: isDarkMode ? 'bg-canvas/80 border-line' : 'bg-surface border-line',
    muted: isDarkMode ? 'text-ink-muted' : 'text-ink-muted',
    title: isDarkMode ? 'text-white' : 'text-ink',
    input: isDarkMode
      ? 'bg-canvas border-line text-ink focus:border-forest/40'
      : 'bg-surface border-line text-ink focus:border-forest/40',
    tabWrap: isDarkMode ? 'bg-canvas border-line' : 'bg-moss border-line',
    tabActive: isDarkMode ? 'bg-surface text-white border-line' : 'bg-surface text-ink border-line ',
    tabIdle: isDarkMode ? 'text-ink-muted hover:text-ink' : 'text-ink-muted hover:text-ink',
    contentBox: isDarkMode ? 'bg-canvas/40 border-line' : 'bg-canvas border-line',
    label: isDarkMode ? 'text-ink-muted' : 'text-ink',
    layoutBtn: isDarkMode
      ? 'bg-surface/50 border-line text-ink-muted hover:border-line hover:bg-surface'
      : 'bg-canvas border-line text-ink-muted hover:border-line hover:bg-surface',
    layoutBtnActive: 'bg-forest/10 border-forest/40 text-forest-ondark dark:text-forest-ondark ',
    paletteBtn: isDarkMode
      ? 'bg-surface/50 hover:bg-surface border-line hover:border-line'
      : 'bg-canvas hover:bg-surface border-line hover:border-line',
    previewCanvas: isDarkMode
      ? ' bg-canvas  '
      : ' bg-moss  ',
    artboard: isDarkMode ? 'border-line bg-canvas' : 'border-line bg-surface ',
    chrome: isDarkMode ? 'bg-canvas border-line' : 'bg-moss border-line',
    chromeUrl: isDarkMode ? 'bg-surface/80 text-ink-muted' : 'bg-surface text-ink-muted border border-line',
    zoomBar: isDarkMode ? 'bg-canvas border-line' : 'bg-surface border-line',
    zoomBtn: isDarkMode ? 'text-ink-muted hover:text-white hover:bg-surface' : 'text-ink-muted hover:text-ink hover:bg-moss',
    loading: isDarkMode ? 'bg-canvas' : 'bg-moss',
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- TRANSLATIONS DICTIONARY ---
  const translations = {
    english: {
      'builder.org': 'Organization',
      'builder.title': 'Landing Page Builder',
      'builder.subtitle': 'Customize your landing page theme, layout, and messaging.',
      'builder.section.layout': 'Layout',
      'builder.section.branding': 'Branding',
      'builder.section.colors': 'Colors',
      'builder.section.content': 'Content',
      'builder.section.publish': 'Publish',
      'builder.layout': 'Choose a layout',
      'builder.palettes': 'Color palette',
      'builder.showMorePalettes': 'Show more palettes',
      'builder.showLessPalettes': 'Show less',
      'builder.messaging': 'Page copy',
      'builder.autoTranslate': 'Auto-translate',
      'builder.processing': 'Translating...',
      'builder.panel': 'panel',
      'builder.lang.hebrew': 'Hebrew',
      'builder.lang.english': 'English',
      'builder.heroTitle': 'Hero title',
      'builder.heroSub': 'Hero subtitle',
      'builder.heroPara': 'Hero description',
      'builder.cta': 'Button label',
      'builder.features': 'Feature bullets (max 3)',
      'builder.featuresTactical': 'Feature bullets (max 4)',
      'builder.addFeature': '+ Add feature',
      'builder.save': 'Save & publish',
      'builder.saving': 'Saving...',
      'builder.success': 'Landing page saved successfully!',
      'builder.preview': 'Live preview',
      'builder.init': 'Loading...',
      'builder.newCompany': 'New company — customize the template below, then save to publish your landing page.',
      'builder.zoomIn': 'Zoom in',
      'builder.zoomOut': 'Zoom out',
      'builder.zoomFit': 'Fit',
      'builder.scarcity': 'Scarcity widget',
      'builder.previewLang': 'Preview language',
      'builder.layout.centered': 'Centered',
      'builder.layout.split': 'Split',
      'builder.layout.tactical': 'Tactical',
      'builder.brandingLogo': 'Company logo',
      'builder.brandingLogoHint': 'Shown in the landing page navigation bar',
    },
    hebrew: {
      'builder.org': 'ארגון',
      'builder.title': 'בונה דף נחיתה',
      'builder.subtitle': 'התאימו את עיצוב דף הנחיתה, הפריסה והתוכן.',
      'builder.section.layout': 'פריסה',
      'builder.section.branding': 'מיתוג',
      'builder.section.colors': 'צבעים',
      'builder.section.content': 'תוכן',
      'builder.section.publish': 'פרסום',
      'builder.layout': 'בחרו פריסה',
      'builder.palettes': 'פלטת צבעים',
      'builder.showMorePalettes': 'הצג עוד פלטות',
      'builder.showLessPalettes': 'הצג פחות',
      'builder.messaging': 'תוכן העמוד',
      'builder.autoTranslate': 'תרגום אוטומטי',
      'builder.processing': 'מתרגם...',
      'builder.panel': 'פאנל',
      'builder.lang.hebrew': 'עברית',
      'builder.lang.english': 'אנגלית',
      'builder.heroTitle': 'כותרת ראשית',
      'builder.heroSub': 'כותרת משנה',
      'builder.heroPara': 'תיאור',
      'builder.cta': 'טקסט כפתור',
      'builder.features': 'נקודות תכונה (עד 3)',
      'builder.featuresTactical': 'נקודות תכונה (עד 4)',
      'builder.addFeature': '+ הוסף תכונה',
      'builder.save': 'שמור ופרסם',
      'builder.saving': 'שומר...',
      'builder.success': 'דף הנחיתה נשמר בהצלחה!',
      'builder.preview': 'תצוגה מקדימה',
      'builder.init': 'טוען...',
      'builder.newCompany': 'חברה חדשה — התאימו את התבנית למטה ושמרו כדי לפרסם את דף הנחיתה.',
      'builder.zoomIn': 'הגדל',
      'builder.zoomOut': 'הקטן',
      'builder.zoomFit': 'התאם',
      'builder.scarcity': 'ווידג\'ט מחסור',
      'builder.previewLang': 'שפת תצוגה',
      'builder.layout.centered': 'מרכזי',
      'builder.layout.split': 'מפוצל',
      'builder.layout.tactical': 'טקטי',
      'builder.brandingLogo': 'לוגו החברה',
      'builder.brandingLogoHint': 'מוצג בסרגל הניווט של דף הנחיתה',
    }
  };
  const t = (key) => translations[language]?.[key] || key;

  const [config, setConfig] = useState({
    ui: {
      layout: 'centered',
      themeSettings: {
        colors: { ...PREMIUM_PALETTES[0].colors },
      },
    },
    content: normalizeLandingContent({}),
  });

  useEffect(() => {
    const initializeBuilderContext = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        setLoadError('');
        const token = await authClient.getAccessToken();
        
        const requestHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };

        const profileResponse = await fetch(`${BUILDER_API_URL}/profiles/me?userId=${user.id}`, { headers: requestHeaders });
        if (!profileResponse.ok) throw new Error('Failed to resolve profile mapping.');
        const profileData = await profileResponse.json();
        setProfile(profileData);

        if (profileData?.company_id) {
          const companyResponse = await fetch(`${BUILDER_API_URL}/companies/${profileData.company_id}`, { headers: requestHeaders });
          if (!companyResponse.ok) throw new Error('Failed to fetch company profile context.');
          const companyData = await companyResponse.json();
          setCompany(companyData);

          const savedConfig = companyData?.config && typeof companyData.config === 'object' ? companyData.config : {};
          setConfig(mergeBuilderConfig(savedConfig));
          setIsNewCompany(!hasSavedLandingConfig(savedConfig));
        } else {
          setIsNewCompany(true);
          setConfig(mergeBuilderConfig({}));
        }
      } catch (err) {
        console.error('Builder initialization fault exception:', err);
        setLoadError(err.message || 'Failed to load your saved landing page.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeBuilderContext();
  }, [user]);

  const effectiveScale = previewZoom === 'fit' ? fitScale : previewZoom;
  const scaledWidth = PREVIEW_ARTBOARD_WIDTH * effectiveScale;
  const scaledHeight = contentHeight * effectiveScale;

  const updatePreviewFit = useCallback(() => {
    const container = previewContainerRef.current;
    const artboard = previewArtboardRef.current;
    if (!container || !artboard) return;

    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const measuredHeight = artboard.scrollHeight || 720;
    setContentHeight(measuredHeight);

    if (previewZoom === 'fit') {
      const horizontalPadding = 32;
      const scaleW = (containerW - horizontalPadding) / PREVIEW_ARTBOARD_WIDTH;
      const nextScale = Math.max(0.35, Math.min(1, scaleW));
      setFitScale(nextScale);
    }
  }, [previewZoom]);

  useEffect(() => {
    updatePreviewFit();
    const container = previewContainerRef.current;
    const artboard = previewArtboardRef.current;
    if (!container || !artboard) return undefined;

    const observer = new ResizeObserver(() => updatePreviewFit());
    observer.observe(container);
    observer.observe(artboard);
    return () => observer.disconnect();
  }, [updatePreviewFit, config, activeLangTab, showScarcityPreview, isLoading]);

  const getCurrentZoomValue = () => (previewZoom === 'fit' ? fitScale : previewZoom);

  const handleZoomOut = () => {
    const current = getCurrentZoomValue();
    setPreviewZoom(Math.max(0.35, Math.round((current - 0.1) * 100) / 100));
  };

  const handleZoomIn = () => {
    const current = getCurrentZoomValue();
    setPreviewZoom(Math.min(1, Math.round((current + 0.1) * 100) / 100));
  };

  const handleZoomFit = () => setPreviewZoom('fit');

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const mockCampaign = showScarcityPreview
    ? {
        isSmartLink: true,
        slotsRemaining: 7,
        expiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      }
    : null;

  const handleContentChange = (newContent) => {
    setConfig((prev) => ({ ...prev, content: newContent }));
    setSaveSuccess(false);
  };

  const handleLayoutChange = (layoutOption) => {
    setConfig((prev) => ({
      ...prev,
      ui: { ...prev.ui, layout: layoutOption },
    }));
    setSaveSuccess(false);
  };

  const handleBrandingChange = (logoUrl) => {
    setConfig((prev) => ({
      ...prev,
      ui: {
        ...prev.ui,
        branding: { ...(prev.ui?.branding || {}), logoUrl },
      },
    }));
    setSaveSuccess(false);
  };

  const runAutoTranslation = async () => {
    try {
      setIsTranslating(true);
      const token = await authClient.getAccessToken();
      
      const sourceLang = activeLangTab;
      const targetLang = sourceLang === 'hebrew' ? 'en' : 'he';
      const targetKey = sourceLang === 'hebrew' ? 'english' : 'hebrew';

      const fieldsToTranslate = [
        ['hero', 'title'],
        ['hero', 'subtitle'],
        ['hero', 'paragraph'],
        ['hero', 'badge'],
        ['hero', 'ctaText'],
        ['benefits', 'sectionTitle'],
        ['benefits', 'sectionSubtitle'],
        ['finalCta', 'title'],
        ['finalCta', 'subtitle'],
        ['finalCta', 'ctaText'],
      ];
      const updatedContent = { ...config.content };

      for (const [section, field] of fieldsToTranslate) {
        const textToTranslate = config.content?.[section]?.[field]?.[sourceLang];
        if (textToTranslate?.trim()) {
          const res = await fetch(`${BUILDER_API_URL}/translate-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ text: textToTranslate, targetLang }),
          });
          const data = await res.json();
          if (data.translatedText) {
            updatedContent[section] = {
              ...updatedContent[section],
              [field]: { ...updatedContent[section]?.[field], [targetKey]: data.translatedText },
            };
          }
        }
      }

      const benefitItems = config.content.benefits?.items?.[sourceLang] || [];
      const translatedBenefits = [];
      for (const item of benefitItems) {
        const title = typeof item === 'string' ? item : item?.title;
        if (title?.trim()) {
          const res = await fetch(`${BUILDER_API_URL}/translate-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ text: title, targetLang }),
          });
          const data = await res.json();
          translatedBenefits.push(
            typeof item === 'string'
              ? data.translatedText || title
              : { ...item, title: data.translatedText || title }
          );
        } else {
          translatedBenefits.push(item);
        }
      }
      updatedContent.benefits = {
        ...updatedContent.benefits,
        items: { ...updatedContent.benefits?.items, [targetKey]: translatedBenefits },
      };

      setConfig(prev => ({ ...prev, content: updatedContent }));
      setSaveSuccess(false);
    } catch (err) {
      console.error('Translation connection exception:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!profile?.company_id) {
      setSaveError('No company is linked to your profile yet.');
      return;
    }
    try {
      setIsSaving(true);
      setSaveSuccess(false);
      setSaveError('');
      
      const token = await authClient.getAccessToken();
      const requestHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const companyUpdateRes = await fetch(`${BUILDER_API_URL}/companies/${profile.company_id}`, {
        method: 'PUT',
        headers: requestHeaders,
        body: JSON.stringify({ config: buildSavePayload(config) }),
      });
      if (!companyUpdateRes.ok) {
        const errBody = await companyUpdateRes.json().catch(() => ({}));
        throw new Error(errBody.error || `Save failed (${companyUpdateRes.status})`);
      }

      const savedCompany = await companyUpdateRes.json();
      if (savedCompany?.config) {
        setCompany(savedCompany);
        setConfig(mergeBuilderConfig(savedCompany.config));
      }
      setIsNewCompany(false);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error('Save configuration exception:', err);
      setSaveError(err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const renderLiveLayoutPreview = () => {
    const SelectedTemplateComponent = getTemplate(config.ui.layout);

    return (
      <div
        ref={previewContainerRef}
        className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-4 pb-8 custom-scrollbar overscroll-contain ${shell.previewCanvas}`}
        dir="ltr"
      >
        <div className="flex justify-center">
          <div
            className="relative shrink-0 overflow-hidden"
            style={{ width: scaledWidth, height: scaledHeight }}
          >
            <div
              ref={previewArtboardRef}
              className={`absolute top-0 left-0 rounded-lg overflow-hidden border  ${shell.artboard}`}
              style={{
                width: PREVIEW_ARTBOARD_WIDTH,
                transform: `scale(${effectiveScale})`,
                transformOrigin: 'top left',
              }}
            >
            <div className={`flex items-center gap-2 px-3 h-10 border-b ${shell.chrome}`}>
              <div className="flex gap-1.5 shrink-0">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-forest/80" />
              </div>
              {config.ui?.branding?.logoUrl ? (
                <img
                  src={config.ui.branding.logoUrl}
                  alt=""
                  className="w-5 h-5 rounded object-contain shrink-0"
                />
              ) : null}
              <div className={`flex-1 mx-2 px-3 py-1 rounded-md text-[10px] font-mono truncate text-center ${shell.chromeUrl}`}>
                {(company?.name || 'yourcompany').toLowerCase().replace(/\s+/g, '')}.betterchoice.app
              </div>
            </div>
            <div dir={previewContentDir}>
              <PreviewLanguageProvider language={activeLangTab}>
                <SelectedTemplateComponent
                  key={`${config.ui.layout}-${activeLangTab}-${isDarkMode ? 'dark' : 'light'}`}
                  config={config}
                  manager={{ name: profile?.name || 'Dietitian Expert' }}
                  campaign={mockCampaign}
                  navigate={(path) => console.log(`Preview Route block trace intercept: ${path}`)}
                  hash="#preview"
                />
              </PreviewLanguageProvider>
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`h-full w-full flex flex-col items-center justify-center gap-3 ${shell.loading}`}>
        <div className="w-10 h-10 rounded-full border-4 border-forest/20 border-t-emerald-500 animate-spin" />
        <span className="text-xs font-mono tracking-widest text-forest-ondark uppercase animate-pulse">{t('builder.init')}</span>
      </div>
    );
  }

  // Determine which palettes to show based on the toggle state
  const visiblePalettes = showAllPalettes ? PREMIUM_PALETTES : PREMIUM_PALETTES.slice(0, 3);

  return (
    <div
      className={`w-full h-full min-h-0 flex flex-col lg:flex-row font-sans overflow-hidden ${shell.page}`}
      dir="ltr"
    >
      {/* CONTROL DASHBOARD */}
      <div className={`w-full lg:w-[45%] xl:w-[42%] border-r flex flex-col min-h-0 flex-1 lg:flex-none overflow-hidden ${shell.sidebar}`} dir={direction}>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar p-4 sm:p-6 flex flex-col gap-4">
          <div>
            <span className="px-2.5 py-1 text-[10px] uppercase font-mono font-bold tracking-wider rounded-md bg-forest/10 border border-forest/20 text-forest-ondark">
              {t('builder.org')}: {company?.name || 'BetterChoice Core'}
            </span>
            <h1 className={`text-2xl font-semibold tracking-tight mt-2 ${shell.title}`}>{t('builder.title')}</h1>
            <p className={`text-sm mt-1 ${shell.muted}`}>{t('builder.subtitle')}</p>
            {isNewCompany && (
              <p className="text-xs text-amber-300/90 mt-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                {t('builder.newCompany')}
              </p>
            )}
            {loadError && (
              <p className="text-xs text-red-700 dark:text-red-400 mt-3 bg-red-700/5 dark:bg-red-400/10 border border-red-700/20 dark:border-red-400/20 rounded-lg px-3 py-2">
                {loadError}
              </p>
            )}
          </div>

          <BuilderSection
            title={t('builder.section.layout')}
            open={openSections.layout}
            onToggle={() => toggleSection('layout')}
            isDarkMode={isDarkMode}
          >
            <p className={`text-[11px] ${shell.muted}`}>{t('builder.layout')}</p>
            <div className="grid grid-cols-3 gap-2">
              {['centered', 'split', 'tactical'].map((layoutOption) => {
                const isActive = config.ui.layout === layoutOption;
                return (
                  <button
                    key={layoutOption}
                    type="button"
                    onClick={() => handleLayoutChange(layoutOption)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all duration-200 ${
                      isActive ? shell.layoutBtnActive : shell.layoutBtn
                    }`}
                  >
                    <LayoutThumbnail type={layoutOption} />
                    <span className="text-[10px] font-bold capitalize">
                      {t(`builder.layout.${layoutOption}`)}
                    </span>
                  </button>
                );
              })}
            </div>
          </BuilderSection>

          <BuilderSection
            title={t('builder.section.branding')}
            open={openSections.branding}
            onToggle={() => toggleSection('branding')}
            isDarkMode={isDarkMode}
          >
            <AssetImageField
              label={t('builder.brandingLogo')}
              hint={t('builder.brandingLogoHint')}
              folder="branding"
              value={config.ui?.branding?.logoUrl || ''}
              onChange={handleBrandingChange}
              shell={shell}
            />
          </BuilderSection>

          <BuilderSection
            title={t('builder.section.colors')}
            open={openSections.colors}
            onToggle={() => toggleSection('colors')}
            isDarkMode={isDarkMode}
          >
            <p className={`text-[11px] ${shell.muted}`}>{t('builder.palettes')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {visiblePalettes.map((palette) => {
                const isSelected =
                  config.ui.themeSettings.colors.primary === palette.colors.primary &&
                  config.ui.themeSettings.colors.secondary === palette.colors.secondary;

                return (
                  <button
                    key={palette.name}
                    type="button"
                    onClick={() => {
                      setConfig((prev) => ({
                        ...prev,
                        ui: { ...prev.ui, themeSettings: { colors: palette.colors } },
                      }));
                      setSaveSuccess(false);
                    }}
                    className={`w-full p-3 rounded-lg border-2 flex flex-col gap-2 transition-all duration-200 ${
                      isSelected ? shell.layoutBtnActive : shell.paletteBtn
                    }`}
                  >
                    <span
                      className={`text-[11px] font-bold text-start leading-tight ${
                        isSelected ? 'text-forest-ondark dark:text-forest-ondark' : shell.label
                      }`}
                    >
                      {palette.name}
                    </span>
                    <div className="flex gap-1.5">
                      {Object.values(palette.colors)
                        .slice(0, 4)
                        .map((hex, i) => (
                          <span
                            key={i}
                            className="w-4 h-4 rounded-full border border-white/30 "
                            style={{ backgroundColor: hex }}
                          />
                        ))}
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setShowAllPalettes(!showAllPalettes)}
              className={`flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors ${shell.muted} hover:opacity-80`}
            >
              {showAllPalettes ? t('builder.showLessPalettes') : t('builder.showMorePalettes')}
              <svg
                className={`w-3 h-3 transition-transform duration-300 ${showAllPalettes ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </BuilderSection>

          <BuilderSection
            title={t('builder.section.content')}
            open={openSections.content}
            onToggle={() => toggleSection('content')}
            isDarkMode={isDarkMode}
          >
            <div className="flex items-center justify-between">
              <p className={`text-[11px] ${shell.muted}`}>{t('builder.messaging')}</p>
              <button
                type="button"
                onClick={runAutoTranslation}
                disabled={isTranslating}
                className="text-[10px] font-bold px-2.5 py-1 bg-forest-ondark hover:bg-forest-ondark/90 text-white dark:text-canvas-dark rounded-md disabled:opacity-40 transition-colors"
              >
                {isTranslating ? t('builder.processing') : t('builder.autoTranslate')}
              </button>
            </div>

            <div className={`flex p-1 rounded-lg border ${shell.tabWrap}`}>
              {['hebrew', 'english'].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setActiveLangTab(lang)}
                  className={`flex-1 py-2 text-xs font-bold capitalize rounded-lg transition-colors border ${
                    activeLangTab === lang ? shell.tabActive : `${shell.tabIdle} border-transparent`
                  }`}
                >
                  {t(`builder.lang.${lang}`)}
                </button>
              ))}
            </div>

            <LandingContentEditor
              content={config.content}
              activeLangTab={activeLangTab}
              shell={shell}
              onChange={handleContentChange}
            />
          </BuilderSection>
        </div>

        {/* Sticky save bar */}
        <div className={`shrink-0 border-t  p-4 flex flex-col gap-2 ${isDarkMode ? 'border-line bg-canvas/95' : 'border-line bg-white/95'}`}>
          <button
            type="button"
            onClick={handleSaveConfig}
            disabled={isSaving}
            className="w-full bg-forest-ondark hover:bg-forest-ondark/90 text-ink font-semibold py-3.5 rounded-lg text-sm transition-all  disabled:opacity-40"
          >
            {isSaving ? t('builder.saving') : t('builder.save')}
          </button>
          {saveSuccess && (
            <div className="w-full bg-forest/10 border border-forest/30 text-forest-ondark text-xs font-bold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-forest-ondark animate-pulse" />
              {t('builder.success')}
            </div>
          )}
          {saveError && (
            <div className="w-full bg-red-700/5 dark:bg-red-400/10 border border-red-700/20 dark:border-red-400/20 text-red-700 dark:text-red-400 text-xs font-bold px-4 py-2.5 rounded-lg text-center">
              {saveError}
            </div>
          )}
        </div>
      </div>

      {/* PREVIEW PANEL — always LTR so zoom/scale math stays correct */}
      <div className={`flex-1 min-h-0 h-full overflow-hidden flex flex-col ${shell.previewPanel}`} dir="ltr">
        <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3 shrink-0 ${shell.previewToolbar}`}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-forest-ondark animate-pulse" />
            <span className={`text-xs font-bold ${shell.label}`}>{t('builder.preview')}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isDarkMode ? 'bg-surface text-ink-muted border-line' : 'bg-moss text-ink-muted border-line'}`}>
              {t(`builder.lang.${activeLangTab}`)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className={`flex items-center gap-2 text-[11px] cursor-pointer select-none ${shell.muted}`}>
              <input
                type="checkbox"
                checked={showScarcityPreview}
                onChange={(e) => setShowScarcityPreview(e.target.checked)}
                className={`rounded text-forest-ondark focus:ring-forest focus:ring-offset-0 ${isDarkMode ? 'border-line bg-surface' : 'border-line bg-surface'}`}
              />
              {t('builder.scarcity')}
            </label>

            <div className={`flex items-center gap-1 border rounded-lg p-0.5 ${shell.zoomBar}`}>
              <button
                type="button"
                onClick={handleZoomOut}
                title={t('builder.zoomOut')}
                className={`w-7 h-7 flex items-center justify-center rounded-md text-sm font-bold transition-colors ${shell.zoomBtn}`}
              >
                −
              </button>
              <span className={`text-[10px] font-mono w-10 text-center ${shell.muted}`}>
                {Math.round(effectiveScale * 100)}%
              </span>
              <button
                type="button"
                onClick={handleZoomIn}
                title={t('builder.zoomIn')}
                className={`w-7 h-7 flex items-center justify-center rounded-md text-sm font-bold transition-colors ${shell.zoomBtn}`}
              >
                +
              </button>
              <button
                type="button"
                onClick={handleZoomFit}
                title={t('builder.zoomFit')}
                className={`px-2 h-7 text-[10px] font-bold rounded-md transition-colors ${
                  previewZoom === 'fit'
                    ? 'bg-forest/20 text-forest-ondark dark:text-forest-ondark'
                    : shell.zoomBtn
                }`}
              >
                {t('builder.zoomFit')}
              </button>
            </div>
          </div>
        </div>

        {renderLiveLayoutPreview()}
      </div>
    </div>
  );
}